import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { getFinancials } from "../../data/request";
import { BALANCE_SHEET_ROWS, getOtherTags, PROFIT_AND_LOSS_ROWS, rowsHaveData } from "../../data/financialsSchema";
import { FinancialsResponse } from "../../types/Financials";
import { DELTA_ACCENT, TEXT_SECONDARY } from "../colors";
import { FinancialsCharts } from "./FinancialsCharts";
import { FinancialsTable, FINANCIALS_SECTION_IDS } from "./FinancialsTable";

const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const FinancialsPanel = (props: { companyNo: string | undefined }) => {
  const { companyNo } = props;
  const [response, setResponse] = useState<FinancialsResponse>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (companyNo == undefined) {
        return;
      }
      setLoading(true);
      const doc = await getFinancials(companyNo);
      setResponse(doc);
      setLoading(false);
    };

    fetchData();
  }, [companyNo]);

  if (loading) {
    return (
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 200,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  const accounts = response?.accounts ?? {};
  if (Object.keys(accounts).length === 0) {
    return (
      <Box style={{ padding: 20 }}>
        <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
          No financial data available for this company. Check the Filing History tab to see
          their filed accounts directly.
        </Typography>
      </Box>
    );
  }

  const sectionLinks = [
    { id: FINANCIALS_SECTION_IDS.balanceSheet, label: "Balance Sheet", visible: rowsHaveData(BALANCE_SHEET_ROWS, accounts) },
    { id: FINANCIALS_SECTION_IDS.profitAndLoss, label: "Profit & Loss", visible: rowsHaveData(PROFIT_AND_LOSS_ROWS, accounts) },
    { id: FINANCIALS_SECTION_IDS.other, label: "Other", visible: getOtherTags(accounts).length > 0 },
  ].filter((section) => section.visible);

  return (
    <Box>
      {sectionLinks.length > 0 && (
        <Box style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "20px 20px 12px" }}>
          {sectionLinks.map((section) => (
            <Chip
              key={section.id}
              label={section.label}
              size="small"
              variant="outlined"
              onClick={() => scrollToSection(section.id)}
              style={{ borderColor: DELTA_ACCENT, color: DELTA_ACCENT }}
            />
          ))}
        </Box>
      )}
      <FinancialsCharts accounts={accounts} />
      <FinancialsTable accounts={accounts} />
    </Box>
  );
};

export default FinancialsPanel;
